import { useState, useEffect, useCallback } from 'react'

/**
 * Reusable async query hook
 * Eliminates duplicate loading/error/data state management
 *
 * @param {Function} queryFn - Async function to execute
 * @param {Array} deps - Dependency array for auto-refetch
 * @param {Object} options - Configuration options
 * @returns {Object} { data, loading, error, refetch }
 */
export const useAsyncQuery = (queryFn, deps = [], options = {}) => {
  const {
    enabled = true,
    onSuccess,
    onError,
    initialData = null
  } = options

  const [data, setData] = useState(initialData)
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState('')

  const execute = useCallback(async () => {
    if (!enabled) return

    try {
      setLoading(true)
      setError('')
      const result = await queryFn()
      setData(result)
      onSuccess?.(result)
      return result
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred'
      setError(errorMessage)
      onError?.(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [queryFn, enabled, onSuccess, onError])

  useEffect(() => {
    if (enabled) {
      execute()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled])

  return {
    data,
    loading,
    error,
    refetch: execute
  }
}

export default useAsyncQuery
